import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { Pencil } from "lucide-react"
import { parseTemplateTokens, type TemplateToken } from "@/lib/template-parameters"
import { ParameterBadge } from "./ParameterBadge"

interface TemplatePromptDisplayProps {
  rawTemplate: string
  parameterValues: Record<string, string>
  onParameterChange: (key: string, value: string) => void
  onExitTemplateMode: () => void
  disabled?: boolean
}

/**
 * 통합 템플릿 에디터 컴포넌트
 *
 * 템플릿 프롬프트를 읽기 전용 텍스트와 인라인 편집 가능한
 * 파라미터 뱃지로 렌더링한다.
 *
 * - 일반 텍스트: 읽기 전용 span
 * - 파라미터: 클릭 가능한 ParameterBadge (Popover로 편집)
 */
export function TemplatePromptDisplay({
  rawTemplate,
  parameterValues,
  onParameterChange,
  onExitTemplateMode,
  disabled = false,
}: TemplatePromptDisplayProps) {
  const { t } = useTranslation()
  const tokens = parseTemplateTokens(rawTemplate)

  // 파라미터가 없으면 null 반환 (일반 Textarea 사용)
  const hasParameters = tokens.some((t) => t.type === "parameter")
  if (!hasParameters) {
    return null
  }

  /**
   * 토큰을 React 요소로 렌더링
   */
  const renderToken = (token: TemplateToken, index: number) => {
    if (token.type === "text") {
      // 줄바꿈을 <br />로 변환
      const parts = token.content.split("\n")
      return (
        <span key={index}>
          {parts.map((part, i) => (
            <span key={i}>
              {part}
              {i < parts.length - 1 && <br />}
            </span>
          ))}
        </span>
      )
    }

    // 파라미터 토큰
    const key = token.key!
    const value = parameterValues[key] ?? token.defaultValue ?? ""

    return (
      <ParameterBadge
        key={index}
        paramKey={key}
        value={value}
        onChange={(newValue) => onParameterChange(key, newValue)}
        disabled={disabled}
      />
    )
  }

  return (
    <div className="space-y-2">
      {/* 템플릿 디스플레이 영역 */}
      <div className="relative min-h-[216px] max-h-[450px] p-4 text-sm leading-7 bg-white border border-slate-200 rounded-md overflow-y-auto whitespace-pre-wrap text-slate-700">
        {tokens.map(renderToken)}
      </div>

      {/* 모드 전환 버튼 */}
      {!disabled && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs text-slate-500 hover:text-slate-700"
            onClick={onExitTemplateMode}
          >
            <Pencil className="w-3 h-3 mr-1" />
            {t("generator.editAsText")}
          </Button>
        </div>
      )}
    </div>
  )
}
